/* ==========================================================================
   Mijn Huisdier Cadeau — Personalisatie (vervanger VO Product Options)
   - Koppelt alle velden aan het productformulier (form-attribuut), zodat ze
     als line item properties in winkelwagen en bestelling komen.
   - Toont/verbergt velden op basis van de gekozen variant
     (bijv. 'Met achterkant gravering').
   - Controleert verplichte velden en bestandsgrootte vóór toevoegen.
   ========================================================================== */
(() => {
  if (window.__mhcPersoLoaded) return;
  window.__mhcPersoLoaded = true;

  const low = (s) => String(s || '').toLowerCase().trim();

  class MhcPersonalisatie extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.maxBytes = (parseFloat(this.dataset.maxMb) || 20) * 1024 * 1024;
      this.msgRequired = this.dataset.msgRequired || 'Dit veld is verplicht.';
      this.msgSize = (this.dataset.msgSize || 'Deze foto is te groot.').replace('[mb]', this.dataset.maxMb || '20');

      try {
        this.options = JSON.parse(this.querySelector('[data-mhc-options]').textContent) || [];
      } catch (e) {
        this.options = [];
      }

      this.section = this.closest('.shopify-section') || document;
      this.bindForm();
      this.bindUploads();
      this.bindVariantWatch();
      this.bindValidation();
      this.applyConditions();

      this.addEventListener('input', (e) => this.clearError(e.target));
      this.addEventListener('change', (e) => this.clearError(e.target));
    }

    get form() {
      return (
        this.section.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[data-type="add-to-cart-form"]')
      );
    }

    inputs() {
      return Array.from(this.querySelectorAll('[data-mhc-input]'));
    }

    /* Velden staan buiten het <form>: koppel ze via het form-attribuut */
    bindForm() {
      const apply = () => {
        const form = this.form;
        if (!form) return false;
        // Let op: form.id kan een <input name="id"> teruggeven, dus het attribuut gebruiken
        let id = form.getAttribute('id');
        if (!id) {
          id = 'mhc-product-form-' + Math.random().toString(36).slice(2, 8);
          form.setAttribute('id', id);
        }
        this.inputs().forEach((i) => i.setAttribute('form', id));
        return true;
      };
      if (!apply()) {
        const obs = new MutationObserver(() => apply() && obs.disconnect());
        obs.observe(this.section, { childList: true, subtree: true });
      }
      // Thema kan het formulier vervangen: opnieuw koppelen
      new MutationObserver(() => {
        const form = this.form;
        if (form && this.inputs().some((i) => i.getAttribute('form') !== form.getAttribute('id'))) apply();
      }).observe(this.section, { childList: true, subtree: true });
    }

    /* Upload: voorbeeld, bestandsnaam en maximale grootte */
    bindUploads() {
      this.querySelectorAll('input[type="file"][data-mhc-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const field = input.closest('[data-field]');
          const preview = field.querySelector('.mhc-p-upload__preview');
          const nameEl = field.querySelector('[data-filename]');
          const cta = field.querySelector('[data-cta]');
          const file = input.files && input.files[0];

          if (!file) {
            field.classList.remove('has-file');
            return;
          }
          if (file.size > this.maxBytes) {
            input.value = '';
            field.classList.remove('has-file');
            this.showError(field, this.msgSize);
            return;
          }

          field.classList.add('has-file');
          if (nameEl) nameEl.textContent = file.name;
          if (cta) cta.textContent = 'Andere foto kiezen';

          if (preview && /^image\/(jpe?g|png|gif|webp)$/i.test(file.type)) {
            const url = URL.createObjectURL(file);
            preview.innerHTML = '';
            const img = document.createElement('img');
            img.alt = '';
            img.src = url;
            img.onload = () => URL.revokeObjectURL(url);
            preview.appendChild(img);
          } else if (preview) {
            preview.innerHTML = '<span class="mhc-p-upload__check">✓</span>';
          }
        });
      });
    }

    /* Huidige keuze per optie, gelezen uit de variantkiezer van het thema */
    selectedValues() {
      const values = {};
      const picker = this.section.querySelector('variant-picker');
      if (picker) {
        picker.querySelectorAll('fieldset input:checked, select').forEach((el) => {
          let pos;
          let val;
          if (el.tagName === 'SELECT') {
            const opt = el.options[el.selectedIndex];
            pos = parseInt((opt && opt.dataset.inputId) || '', 10);
            val = el.value;
          } else {
            pos = parseInt(el.dataset.inputId || '', 10);
            val = el.value;
          }
          if (!isNaN(pos) && this.options[pos - 1] !== undefined) values[low(this.options[pos - 1])] = val;
        });
      }
      return values;
    }

    /* Toon/verberg velden op basis van woorden in optienamen (bijv. 'naam', 'achterkant') */
    applyConditions() {
      const selected = this.selectedValues();
      const optionNames = this.options.map(low);

      this.querySelectorAll('[data-field][data-depends]').forEach((field) => {
        const keywords = (field.dataset.depends || '').split(',').map(low).filter(Boolean);
        if (!keywords.length) return;

        let visible = field.dataset.dependsDefault !== 'hide';
        let forcedRequired = false;
        let position = '';

        for (const kw of keywords) {
          const optName = optionNames.find((n) => n.includes(kw));
          if (!optName) continue;
          const val = low(selected[optName]);
          if (!val) continue;
          if (val.startsWith('zonder') || val === 'nee') {
            visible = false;
          } else if (val.startsWith('met') || val === 'ja') {
            visible = true;
            forcedRequired = true;
            position = kw;
            break;
          }
        }

        field.hidden = !visible;
        field.dataset.forcedRequired = forcedRequired ? 'true' : 'false';
        field.querySelectorAll('[data-mhc-input]').forEach((input) => {
          input.disabled = !visible;
          if (!visible) this.clearError(input);
        });

        // Label en propertynaam aanpassen aan de positie (achterkant/zijkant)
        const tpl = field.dataset.labelTemplate;
        if (tpl && position) {
          const text = tpl.replace('[positie]', position);
          const labelText = field.querySelector('[data-label-text]');
          if (labelText) labelText.textContent = text;
          field.querySelectorAll('[data-dynamic-name]').forEach((input) => {
            input.name = 'properties[' + text + ']';
          });
        }
      });
    }

    bindVariantWatch() {
      const run = () => {
        // Direct én na het bijwerken van de variantkiezer door het thema
        setTimeout(() => this.applyConditions(), 0);
        setTimeout(() => this.applyConditions(), 400);
      };
      this.section.addEventListener('change', (e) => {
        if (e.target.closest && e.target.closest('variant-picker')) run();
      });
      const picker = this.section.querySelector('variant-picker');
      if (picker) {
        new MutationObserver(run).observe(picker, { subtree: true, attributes: true, attributeFilter: ['checked', 'selected'], childList: true });
      }
      document.addEventListener('variant:update', run);
    }

    isRequired(field) {
      return field.dataset.required === 'true' || field.dataset.forcedRequired === 'true';
    }

    isFilled(field) {
      const inputs = Array.from(field.querySelectorAll('[data-mhc-input]'));
      if (!inputs.length) return true;
      const first = inputs[0];
      if (first.type === 'file') return !!(first.files && first.files.length);
      if (first.type === 'radio') return inputs.some((i) => i.checked);
      return inputs.some((i) => i.value.trim() !== '');
    }

    validate() {
      let firstInvalid = null;
      this.querySelectorAll('[data-field]').forEach((field) => {
        if (field.hidden) return;
        if (this.isRequired(field) && !this.isFilled(field)) {
          this.showError(field, this.msgRequired);
          if (!firstInvalid) firstInvalid = field;
        }
      });
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = firstInvalid.querySelector('[data-mhc-input]');
        if (input) setTimeout(() => input.focus({ preventScroll: true }), 350);
        return false;
      }
      return true;
    }

    bindValidation() {
      const block = (event) => {
        if (this.validate()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      document.addEventListener(
        'submit',
        (event) => {
          if (event.target !== this.form) return;
          if (!this.validate()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          this.skipEmpties();
        },
        true
      );
      document.addEventListener(
        'click',
        (event) => {
          const btn = event.target.closest && event.target.closest('button[name="add"], button[type="submit"]');
          if (!btn || !this.form || !(btn.form === this.form || this.form.contains(btn))) return;
          block(event);
          if (!event.defaultPrevented) this.skipEmpties();
        },
        true
      );
    }

    /* Lege, optionele tekstvelden niet meesturen (schonere bestellingen) */
    skipEmpties() {
      const empties = this.inputs().filter(
        (i) => !i.disabled && (i.type === 'text' || i.tagName === 'TEXTAREA') && i.value.trim() === ''
      );
      if (!empties.length) return;
      empties.forEach((i) => (i.disabled = true));
      setTimeout(() => empties.forEach((i) => (i.disabled = false)), 1500);
    }

    showError(field, msg) {
      field.classList.add('is-invalid');
      const el = field.querySelector('[data-error]');
      if (el) {
        el.textContent = msg;
        el.hidden = false;
      }
    }

    clearError(target) {
      const field = target && target.closest ? target.closest('[data-field]') : null;
      if (!field) return;
      field.classList.remove('is-invalid');
      const el = field.querySelector('[data-error]');
      if (el) el.hidden = true;
    }
  }

  if (!customElements.get('mhc-personalisatie')) customElements.define('mhc-personalisatie', MhcPersonalisatie);
})();
